import { APP_TIMEZONE } from "@/config/timezone";
import type { AssistantLogText } from "@/locales/assistant-log";
import type {
	AssistantLogCostDto,
	AssistantLogModality,
	AssistantLogStatus,
} from "@/types/ai-assistant-log";

/**
 * Logika murni tampilan "Log Percakapan": format angka, tanggal/jam WITA, dan
 * label filter. Semua waktu datang sebagai ISO UTC dari server dan
 * ditampilkan di zona desa (`APP_TIMEZONE`), bukan zona browser — supaya jam
 * yang dibaca admin sama dengan jam yang dibaca operator desa.
 *
 * Warna lencana vendor memakai ulang `VENDOR_COLOR` dari `usage-log.logic.ts`
 * (ungu Claude / tosca ChatGPT) agar dua halaman tidak pernah beda warna.
 */

export type Lang = "id" | "en";

const locale = (lang: Lang) => (lang === "en" ? "en-US" : "id-ID");

/** Angka jumlah/token; null → tanda hubung. */
export function formatLogCount(
	n: number | null | undefined,
	lang: Lang = "id",
): string {
	return n === null || n === undefined ? "—" : n.toLocaleString(locale(lang));
}

/** Pasangan token masuk/keluar untuk satu kolom. */
export function formatLogTokens(
	input: number,
	output: number,
	lang: Lang = "id",
): string {
	return `${formatLogCount(input, lang)} / ${formatLogCount(output, lang)}`;
}

/**
 * Estimasi dolar. null = tarif belum diisi admin (bukan nol), jadi yang
 * ditampilkan teks "tarif belum diisi", bukan "$0".
 */
export function formatLogCost(
	cost: AssistantLogCostDto,
	t: AssistantLogText,
	lang: Lang = "id",
): string {
	if (cost.usd === null) return t.costUnknown;
	const value = cost.usd.toLocaleString(locale(lang), {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 2,
		maximumFractionDigits: cost.usd < 1 ? 4 : 2,
	});
	return cost.incomplete ? `≈ ${value}` : value;
}

const dateFormat = (lang: Lang) =>
	new Intl.DateTimeFormat(locale(lang), {
		timeZone: APP_TIMEZONE,
		day: "numeric",
		month: "short",
		year: "numeric",
	});

const timeFormat = (lang: Lang) =>
	new Intl.DateTimeFormat(locale(lang), {
		timeZone: APP_TIMEZONE,
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	});

/** Tanggal WITA, mis. "20 Okt 2026". */
export function formatLogDate(iso: string, lang: Lang = "id"): string {
	return dateFormat(lang).format(new Date(iso));
}

/** Jam WITA, mis. "09:14". */
export function formatLogTime(iso: string, lang: Lang = "id"): string {
	return timeFormat(lang).format(new Date(iso));
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Waktu relatif kasar ("3 jam lalu"). Lebih dari 7 hari jatuh ke tanggal
 * absolut supaya tidak ada "37 hari lalu" yang sulit dibaca.
 */
export function formatLogRelative(
	iso: string | null,
	lang: Lang = "id",
	now: Date = new Date(),
): string {
	if (!iso) return "—";
	const diff = now.getTime() - new Date(iso).getTime();
	if (diff < MINUTE_MS) return lang === "en" ? "just now" : "baru saja";
	const rtf = new Intl.RelativeTimeFormat(locale(lang), { numeric: "auto" });
	if (diff < HOUR_MS)
		return rtf.format(-Math.floor(diff / MINUTE_MS), "minute");
	if (diff < DAY_MS) return rtf.format(-Math.floor(diff / HOUR_MS), "hour");
	const days = Math.floor(diff / DAY_MS);
	const MAX_RELATIVE_DAYS = 7;
	return days <= MAX_RELATIVE_DAYS
		? rtf.format(-days, "day")
		: formatLogDate(iso, lang);
}

/** Latensi ms → detik satu desimal; null → tanda hubung. */
export function formatLogLatency(ms: number | null, lang: Lang = "id"): string {
	if (ms === null) return "—";
	const MS_PER_SECOND = 1000;
	return `${(ms / MS_PER_SECOND).toLocaleString(locale(lang), {
		maximumFractionDigits: 1,
	})} s`;
}

export function logStatusLabel(
	status: AssistantLogStatus,
	t: AssistantLogText,
): string {
	if (status === "error") return t.statusError;
	if (status === "limited") return t.statusLimited;
	return t.statusOk;
}

export function logModalityLabel(
	modality: AssistantLogModality,
	t: AssistantLogText,
): string {
	return modality === "voice" ? t.modalityVoice : t.modalityText;
}

/** Inisial untuk avatar; dari nama bila ada, selain itu dari email. */
export function logInitials(name: string | null, email: string): string {
	const source = name?.trim() || email;
	const MAX_INITIALS = 2;
	const parts = source.split(/[\s@._-]+/).filter(Boolean);
	return (
		parts
			.slice(0, MAX_INITIALS)
			.map((p) => p[0]?.toUpperCase() ?? "")
			.join("") || "?"
	);
}

/** Nama tampilan pengguna; jatuh ke email bila nama belum diisi. */
export function logDisplayName(name: string | null, email: string): string {
	return name?.trim() || email;
}
